import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  FaArrowLeft,
  FaCalendarAlt,
  FaClock,
  FaUsers,
  FaTeam,
  FaPlus,
  FaTimes,
  FaCheck,
  FaMapMarkerAlt,
} from 'react-icons/fa';
import Navbar from '../../components/Navbar';
import Footer from '../../components/Footer';
import './ModernBookingPage.css';

const ModernBookingPage = () => {
  const navigate = useNavigate();
  const { roomId } = useParams();

  // Mock room data
  const mockRooms = {
    'room-1': {
      id: 'room-1',
      name: 'Executive Boardroom',
      location: 'Bangalore, Koramangala',
      capacity: 12,
      price: 1200,
    },
    '1': {
      id: '1',
      name: 'Executive Boardroom',
      location: 'Bangalore, Koramangala',
      capacity: 12,
      price: 1200,
    },
    'sample-1': {
      id: 'sample-1',
      name: 'Executive Boardroom',
      location: 'Bangalore, Koramangala',
      capacity: 12,
      price: 1200,
    },
  };

  // Form state
  const [selectedRoom, setSelectedRoom] = useState(roomId || 'room-1');
  const [selectedDate, setSelectedDate] = useState('');
  const [selectedStartTime, setSelectedStartTime] = useState('');
  const [selectedEndTime, setSelectedEndTime] = useState('');
  const [selectedTeam, setSelectedTeam] = useState('');
  const [attendees, setAttendees] = useState('');
  const [additionalMembers, setAdditionalMembers] = useState([]);
  const [notes, setNotes] = useState('');

  // Get current room
  const currentRoom = mockRooms[selectedRoom] || mockRooms['room-1'];

  // Time slots
  const timeSlots = [
    '09:00', '09:30', '10:00', '10:30', '11:00', '11:30',
    '12:00', '12:30', '13:00', '13:30', '14:00', '14:30',
    '15:00', '15:30', '16:00', '16:30', '17:00', '17:30',
    '18:00', '18:30', '19:00', '19:30', '20:00', '20:30'
  ];

  // Get slot status
  const getSlotStatus = (time) => {
    if (!selectedDate) return 'available';
    
    const now = new Date();
    const slotDate = new Date(selectedDate);
    const [hours, minutes] = time.split(':');
    slotDate.setHours(parseInt(hours), parseInt(minutes), 0);
    
    if (slotDate < now) return 'past';
    
    // Mock booked slots
    const bookedSlots = ['09:00', '09:30', '14:00', '14:30', '18:00'];
    return bookedSlots.includes(time) ? 'booked' : 'available';
  };

  // Handle slot click
  const handleSlotClick = (time) => {
    if (getSlotStatus(time) === 'available') {
      setSelectedStartTime(time);
      
      // Auto-set end time to 1 hour later
      const [hours, minutes] = time.split(':');
      let endHours = parseInt(hours) + 1;
      let endMinutes = parseInt(minutes);
      
      if (endHours >= 24) {
        endHours = 23;
        endMinutes = 30;
      }
      
      const endTime = `${String(endHours).padStart(2, '0')}:${String(endMinutes).padStart(2, '0')}`;
      setSelectedEndTime(endTime);
    }
  };

  // Handle add member
  const handleAddMember = () => {
    setAdditionalMembers([...additionalMembers, '']);
  };

  // Handle remove member
  const handleRemoveMember = (index) => {
    setAdditionalMembers(additionalMembers.filter((_, i) => i !== index));
  };

  // Handle member change
  const handleMemberChange = (index, value) => {
    const updated = [...additionalMembers];
    updated[index] = value;
    setAdditionalMembers(updated);
  };

  // Handle booking
  const handleConfirmBooking = () => {
    if (!selectedDate || !selectedStartTime || !selectedEndTime) {
      alert('Please select date and time');
      return;
    }
    alert('Booking confirmed! Check your email for confirmation.');
    navigate('/');
  };

  // Get available slots count
  const availableSlotsCount = timeSlots.filter(
    (slot) => getSlotStatus(slot) === 'available'
  ).length;

  return (
    <div className="modern-booking-page">
      <Navbar />

      <div className="mbp-container">
        {/* Header */}
        <motion.div
          className="mbp-header"
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <button
            className="mbp-back-btn"
            onClick={() => navigate(-1)}
          >
            <FaArrowLeft /> Back
          </button>
          <div>
            <h1>Book Your Meeting Room</h1>
            <p>Select your preferred date and time to reserve the space</p>
          </div>
        </motion.div>

        {/* Main Content - Split Layout */}
        <div className="mbp-content">
          {/* Left: Booking Form */}
          <motion.div
            className="mbp-left-panel"
            initial={{ opacity: 0, x: -30 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.5, delay: 0.1 }}
          >
            <div className="mbp-form-card">
              <div className="mbp-form-header">
                <FaCalendarAlt className="mbp-form-icon" />
                <h2>Booking Details</h2>
              </div>

              <form className="mbp-form">
                {/* Room Selection */}
                <div className="mbp-form-group">
                  <label>Select Room *</label>
                  <select
                    value={selectedRoom}
                    onChange={(e) => setSelectedRoom(e.target.value)}
                    className="mbp-form-input"
                  >
                    <option value="room-1">Executive Boardroom</option>
                    <option value="1">Training Room</option>
                    <option value="sample-1">Collaboration Suite</option>
                  </select>
                </div>

                {/* Room Info */}
                <div className="mbp-room-info">
                  <div className="mbp-room-info-item">
                    <FaMapMarkerAlt />
                    <span>{currentRoom.location}</span>
                  </div>
                  <div className="mbp-room-info-item">
                    <FaUsers />
                    <span>Up to {currentRoom.capacity} people</span>
                  </div>
                  <div className="mbp-room-info-item">
                    <FaClock />
                    <span>₹{currentRoom.price}/hour</span>
                  </div>
                </div>

                {/* Date Picker */}
                <div className="mbp-form-group">
                  <label>Select Date *</label>
                  <input
                    type="date"
                    value={selectedDate}
                    onChange={(e) => setSelectedDate(e.target.value)}
                    className="mbp-form-input"
                    min={new Date().toISOString().split('T')[0]}
                  />
                </div>

                {/* Time Selection */}
                <div className="mbp-form-row">
                  <div className="mbp-form-group">
                    <label>Start Time *</label>
                    <select
                      value={selectedStartTime}
                      onChange={(e) => setSelectedStartTime(e.target.value)}
                      className="mbp-form-input"
                    >
                      <option value="">Select start time</option>
                      {timeSlots.map((time) => (
                        <option key={time} value={time}>
                          {time}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className="mbp-form-group">
                    <label>End Time *</label>
                    <select
                      value={selectedEndTime}
                      onChange={(e) => setSelectedEndTime(e.target.value)}
                      className="mbp-form-input"
                    >
                      <option value="">Select end time</option>
                      {timeSlots.map((time) => (
                        <option key={time} value={time}>
                          {time}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                {/* Team Selection */}
                <div className="mbp-form-group">
                  <label>Select Team</label>
                  <select
                    value={selectedTeam}
                    onChange={(e) => setSelectedTeam(e.target.value)}
                    className="mbp-form-input"
                  >
                    <option value="">No team selected</option>
                    <option value="engineering">Engineering Team</option>
                    <option value="sales">Sales Team</option>
                    <option value="marketing">Marketing Team</option>
                    <option value="hr">HR Team</option>
                  </select>
                </div>

                {/* Attendees */}
                <div className="mbp-form-group">
                  <label>Number of Attendees</label>
                  <input
                    type="number"
                    value={attendees}
                    onChange={(e) => setAttendees(e.target.value)}
                    className="mbp-form-input"
                    placeholder="Enter number of attendees"
                    min="1"
                    max={currentRoom.capacity}
                  />
                </div>

                {/* Additional Members */}
                <div className="mbp-additional-members">
                  <label>Add Additional Members <span className="mbp-optional">(Optional)</span></label>
                  {additionalMembers.map((member, idx) => (
                    <div key={idx} className="mbp-member-input-group">
                      <input
                        type="text"
                        value={member}
                        onChange={(e) => handleMemberChange(idx, e.target.value)}
                        placeholder="Member name or email"
                        className="mbp-form-input"
                      />
                      <button
                        type="button"
                        className="mbp-remove-member-btn"
                        onClick={() => handleRemoveMember(idx)}
                      >
                        <FaTimes />
                      </button>
                    </div>
                  ))}
                  <button
                    type="button"
                    className="mbp-add-member-btn"
                    onClick={handleAddMember}
                  >
                    <FaPlus /> Add Member
                  </button>
                </div>

                {/* Notes */}
                <div className="mbp-form-group">
                  <label>Additional Notes</label>
                  <textarea
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                    className="mbp-form-input mbp-textarea"
                    placeholder="Any special requirements or notes..."
                    rows="3"
                  />
                </div>

                {/* Confirm Button */}
                <button
                  type="button"
                  className="mbp-confirm-btn"
                  onClick={handleConfirmBooking}
                >
                  <FaCheck /> Confirm Booking
                </button>
              </form>
            </div>
          </motion.div>

          {/* Right: Availability Panel */}
          <motion.div
            className="mbp-right-panel"
            initial={{ opacity: 0, x: 30 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.5, delay: 0.1 }}
          >
            <div className="mbp-availability-card">
              <div className="mbp-availability-header">
                <FaClock className="mbp-availability-icon" />
                <h2>Room Availability</h2>
              </div>

              {selectedDate ? (
                <div className="mbp-availability-content">
                  <div className="mbp-date-info">
                    <span>Available slots for</span>
                    <strong>{selectedDate}</strong>
                    <span className="mbp-slots-count">
                      {availableSlotsCount} slots available
                    </span>
                  </div>

                  <div className="mbp-time-slots-grid">
                    {timeSlots.map((time) => {
                      const status = getSlotStatus(time);
                      return (
                        <motion.button
                          key={time}
                          type="button"
                          className={`mbp-time-slot mbp-slot-${status}`}
                          onClick={() => handleSlotClick(time)}
                          disabled={status !== 'available'}
                          whileHover={status === 'available' ? { scale: 1.05 } : {}}
                          whileTap={status === 'available' ? { scale: 0.95 } : {}}
                          title={
                            status === 'past'
                              ? 'Past time'
                              : status === 'booked'
                              ? 'Already booked'
                              : 'Available'
                          }
                        >
                          <span className="mbp-slot-time">{time}</span>
                          <span className="mbp-slot-status">
                            {status === 'available' && '✓ Available'}
                            {status === 'booked' && '✗ Booked'}
                            {status === 'past' && '⊘ Past'}
                          </span>
                        </motion.button>
                      );
                    })}
                  </div>

                  <div className="mbp-legend">
                    <div className="mbp-legend-item">
                      <span className="mbp-legend-dot mbp-legend-available"></span>
                      <span>Available</span>
                    </div>
                    <div className="mbp-legend-item">
                      <span className="mbp-legend-dot mbp-legend-booked"></span>
                      <span>Booked</span>
                    </div>
                    <div className="mbp-legend-item">
                      <span className="mbp-legend-dot mbp-legend-past"></span>
                      <span>Past</span>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="mbp-empty-state">
                  <FaCalendarAlt className="mbp-empty-icon" />
                  <p>Select a date to view available time slots</p>
                </div>
              )}
            </div>
          </motion.div>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default ModernBookingPage;
